import {
    BillingRunParams,
    Invoice,
    InvoiceLine,
    ProjectCheckResult,
    ProjectCheckTraffic,
    ServiceItem,
    ServiceTransaction,
    TaxCategoryCode,
} from '../types';
import {
    computeOpenPositions,
    cumulativeDeliveryStart,
    groupTaxes,
    OpenPosition,
} from './invoiceLogic';
import { buchungstag, isPostingEntry, kennungFuerVergleich } from './serviceLogUtils';
import { allocatedByEntry } from './allocationLogic';
import { Cents, sumCents } from './money';

/**
 * Rechnungslauf — "Sammelrechnung" im Sprachgebrauch des Hauses.
 *
 * Auswahl von Projekten und/oder einem Zeitraum, daraus je Kunde ein
 * Rechnungsentwurf, der anschliessend in einer Pruefqueue landet. Nichts wird
 * automatisch ausgestellt.
 *
 * Diese Datei ist pur: keine Datenbank, kein Date.now(). Der Trockenlauf ist
 * damit exakt dasselbe Stueck Logik wie die Ausfuehrung — der Nutzer sieht
 * vorher genau das, was danach entsteht, und nicht eine Schaetzung davon.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Eingaben
// ═══════════════════════════════════════════════════════════════════════════

export interface BillingCandidate {
    projectId: string;
    projectNumber?: string;
    projectName: string;
    customerId?: string;
    customerName?: string;
    services: ServiceItem[];
    serviceLog?: ServiceTransaction[];
    /**
     * Belege dieses Projekts.
     *
     * Sie tragen die festgeschriebene Zuordnung zu den Protokolleintraegen.
     * Ohne sie kappt der Lauf den Zeitraum wie frueher auf die volle dort
     * protokollierte Menge und bietet einen bereits abgerechneten Zeitraum
     * erneut an.
     */
    invoices?: Pick<Invoice, 'lines'>[];
    /**
     * Rechnet DIESES Projekt kumulativ ab?
     *
     * Nicht jedes tut das. Massgeblich ist, wie die bereits ausgestellten
     * Belege des Projekts gebaut sind — ein Lauf darf ein Projekt nicht auf ein
     * anderes Modell umstellen, nur weil er global so eingestellt waere. Ohne
     * Vorbelege entscheidet die Voreinstellung.
     */
    cumulative?: boolean;
    /** Mengen, die ein anderer offener Entwurf bereits beansprucht. */
    reservations?: Map<string, number>;
    /** Ergebnis des Projektchecks. Fehlt es, gilt das Projekt als ungeprueft. */
    check?: ProjectCheckResult;
    isChild?: boolean;
    /**
     * Vertraglich vereinbarter Sicherheitseinbehalt des Projekts in Prozent
     * (ProjectGlobalData.retention). Fehlender Eintrag und 0 sind dasselbe:
     * "kein Einbehalt vereinbart".
     */
    retentionPercent?: number;
}

export type ExclusionReason =
    | 'no_customer'
    | 'child_project'
    | 'check_red'
    | 'check_yellow'
    | 'check_missing'
    | 'nothing_open'
    | 'below_minimum'
    | 'outside_period';

export interface BillingRunExclusion {
    projectId: string;
    projectName: string;
    reason: ExclusionReason;
    /** Klartext fuer den Bericht — der Nutzer soll nie raten muessen. */
    detail: string;
}

export interface BillingRunProject {
    projectId: string;
    projectNumber?: string;
    projectName: string;
    positions: OpenPosition[];
    netCents: Cents;
    traffic: ProjectCheckTraffic;
    findingCount: number;
    /** Satz des Projekts — Grundlage von retentionForGroup. */
    retentionPercent?: number;
}

export interface BillingRunGroup {
    /** Schluessel der Gruppe: Kunden- bzw. Projekt-ID, je nach groupBy. */
    key: string;
    customerId: string;
    customerName?: string;
    projects: BillingRunProject[];
    lines: InvoiceLine[];
    netCents: Cents;
    taxCents: Cents;
    grossCents: Cents;
    /** Schlechteste Ampel der enthaltenen Projekte. */
    traffic: ProjectCheckTraffic;
    findingCount: number;
    /**
     * Wird dieser Beleg kumulativ gebaut?
     *
     * Folgt dem Projekt, nicht dem Lauf. Kumulative Gruppen enthalten immer
     * genau ein Projekt — den Gesamtstand gibt es nur je Projekt.
     */
    cumulative: boolean;
    /**
     * Sicherheitseinbehalt dieses Belegs — ein Beleg, ein Satz.
     * `conflict` gesetzt heisst: dieser Beleg darf nicht entstehen.
     */
    retention: GroupRetention;
}

export interface BillingRunPreview {
    groups: BillingRunGroup[];
    excluded: BillingRunExclusion[];
    totals: {
        groups: number;
        projects: number;
        netCents: Cents;
        grossCents: Cents;
    };
}

const EXCLUSION_TEXT: Record<ExclusionReason, string> = {
    no_customer: 'Kein Kunde am Projekt — ohne Rechnungsempfänger kann keine Rechnung entstehen.',
    child_project: 'Unterprojekt — das Leistungsverzeichnis liegt am übergeordneten Projekt.',
    check_red: 'Projektcheck gesperrt (rot).',
    check_yellow: 'Projektcheck auffällig (gelb) und gelbe Projekte sind nicht ausgewählt.',
    check_missing: 'Kein Projektcheck vorhanden — ungeprüft wird nicht abgerechnet.',
    nothing_open: 'Keine offene Position.',
    below_minimum: 'Unterhalb der Bagatellgrenze.',
    outside_period: 'Keine Leistung im gewählten Zeitraum.',
};

// ═══════════════════════════════════════════════════════════════════════════
// Zeitraum
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Menge, die im gewaehlten Zeitraum erbracht und noch NICHT abgerechnet ist.
 *
 * Frueher stand hier die volle im Zeitraum protokollierte Menge, weil
 * quantityBilled nur eine Summe je Position ist und nicht verriet, welcher
 * Eintrag bereits abgerechnet war. Der Lauf rechnete deshalb hoechstens so viel
 * ab, wie im Zeitraum geleistet wurde — und bot denselben Zeitraum bei jedem
 * weiteren Lauf erneut an, solange im Projekt ueberhaupt etwas offen war.
 *
 * Mit den Belegen des Projekts ist die Frage beantwortet: jede ausgestellte
 * Rechnung traegt, welche Eintraege sie mit welcher Teilmenge abgerechnet hat.
 * Ohne Belege bleibt es beim alten Verhalten.
 */
export const quantityInPeriod = (
    serviceId: string,
    log: ServiceTransaction[],
    from?: string,
    to?: string,
    invoices?: Pick<Invoice, 'lines'>[],
): number => {
    if (!from && !to) return Number.POSITIVE_INFINITY;
    const allocated = invoices?.length ? allocatedByEntry(invoices) : null;
    return log
        .filter((t) => {
            // Auf den Kalendertag kuerzen: das Protokoll fuehrt volle
            // Zeitstempel, die Zeitraumgrenzen sind reine Tage. Der BERLINER
            // Tag (Betreiber-Entscheid 1 vom 02.10.2026) — dieselbe Regel wie
            // die Zuordnung (allocationLogic.orderForPeriod), sonst boete der
            // Lauf eine Begehung von 00:30 Uhr am Monatsersten im Vormonat an.
            const day = buchungstag(t.date);
            return (
                t.serviceId === serviceId &&
                // Buchungen sind keine Arbeit — die Rueckschreibung einer
                // Rechnung so wenig wie eine manuelle Verbuchung.
                !isPostingEntry(t) &&
                !!day &&
                (!from || day >= from) &&
                (!to || day <= to)
            );
        })
        .reduce((sum, t) => {
            const quantity = Number(t.quantity) || 0;
            // Nur Zugaenge sind zugeordnet; eine Korrektur nach unten bleibt in
            // voller Hoehe stehen und mindert die Menge des Zeitraums.
            // Nachgeschlagen wie allocatedByEntry die Map fuellt
            // (`kennungFuerVergleich`) — eine uuid in anderer Schreibweise ist
            // derselbe Eintrag, kein offener.
            const open =
                allocated && quantity > 0
                    ? Math.max(0, quantity - (allocated.get(kennungFuerVergleich(t.id)) ?? 0))
                    : quantity;
            return sum + open;
        }, 0);
};

// ═══════════════════════════════════════════════════════════════════════════
// Sicherheitseinbehalt
// ═══════════════════════════════════════════════════════════════════════════

export interface GroupRetention {
    /** Einheitlicher Satz aller Projekte der Gruppe. 0 = kein Einbehalt. */
    percent: number;
    /**
     * Klartext, wenn die Projekte verschiedene Saetze tragen. Gesetzt heisst:
     * dieser Beleg entsteht nicht.
     */
    conflict?: string;
}

export const RETENTION_CONFLICT_TEXT =
    'Projekte mit abweichendem Sicherheitseinbehalt können nicht auf einer Sammelrechnung stehen';

/**
 * Der Sicherheitseinbehalt einer Gruppe — ein Beleg, ein Satz.
 *
 * Der Einbehalt ist eine Vertragsbedingung des einzelnen Projekts, der Beleg
 * kennt aber nur EINEN Satz (InvoiceDetails.retentionPercent) und rechnet ihn
 * auf den gesamten Rechnungsbetrag. Zwei Projekte mit 5 % und 0 % auf einem
 * Beleg liessen sich also gar nicht richtig abrechnen: welchen Satz man auch
 * naehme, einer der beiden Auftraege bekaeme den falschen. Deshalb faellt der
 * Beleg aus dem Lauf, statt still den Satz des ersten Projekts zu nehmen.
 *
 * Fehlender Eintrag und 0 sind dasselbe. Ohne diese Gleichsetzung meldete jede
 * Gruppe aus einem Projekt mit Eintrag "0" und einem ohne Eintrag einen
 * Konflikt, obwohl beide dasselbe bedeuten.
 */
export const retentionForGroup = (
    projects: Array<{ projectNumber?: string; projectName: string; retentionPercent?: number }>,
): GroupRetention => {
    const rate = (p: { retentionPercent?: number }): number =>
        typeof p.retentionPercent === 'number' && Number.isFinite(p.retentionPercent)
            ? Math.max(0, p.retentionPercent)
            : 0;

    const first = projects.length ? rate(projects[0]) : 0;
    if (projects.every((p) => rate(p) === first)) return { percent: first };

    // Die Projektnummern gehoeren in die Meldung: der Bearbeiter muss wissen,
    // welches Projekt er anfassen oder aus dem Lauf nehmen muss. Der Satz
    // dahinter erspart ihm das Nachschlagen in jedem einzelnen.
    const named = projects
        .map(
            (p) =>
                `${p.projectNumber || p.projectName} (${rate(p).toLocaleString('de-DE', {
                    maximumFractionDigits: 2,
                })} %)`,
        )
        .join(', ');
    return { percent: first, conflict: `${RETENTION_CONFLICT_TEXT}: ${named}` };
};

// ═══════════════════════════════════════════════════════════════════════════
// Trockenlauf
// ═══════════════════════════════════════════════════════════════════════════

const trafficRank: Record<ProjectCheckTraffic, number> = { green: 0, yellow: 1, red: 2 };

const worstTraffic = (values: ProjectCheckTraffic[]): ProjectCheckTraffic =>
    values.reduce<ProjectCheckTraffic>(
        (worst, t) => (trafficRank[t] > trafficRank[worst] ? t : worst),
        'green',
    );

export const previewBillingRun = (
    candidates: BillingCandidate[],
    params: BillingRunParams,
    makeLineId: () => string,
    /**
     * Steuersatz in Basispunkten fuer die erzeugten Zeilen.
     *
     * Muss aus der Konfiguration kommen. Fest verdrahtete 19 % wiesen bei einem
     * Kleinunternehmer (defaultTaxRateBp = 0) auf JEDER Sammelrechnung Steuer
     * aus, die nach § 14c UStG dann auch geschuldet wird — und validateInvoice
     * schlaegt nicht an, weil die Nullsatz-Pruefung nur fuer Gruppen mit Satz 0
     * greift. Der manuelle Weg macht es laengst richtig.
     */
    defaultTaxRateBp = 1900,
): BillingRunPreview => {
    const runTaxCategory: TaxCategoryCode = defaultTaxRateBp > 0 ? 'S' : 'Z';
    const excluded: BillingRunExclusion[] = [];
    const usable: Array<
        BillingRunProject & { customerId: string; customerName?: string; cumulative: boolean }
    > = [];

    const exclude = (c: BillingCandidate, reason: ExclusionReason, extra?: string) => {
        excluded.push({
            projectId: c.projectId,
            projectName: c.projectName,
            reason,
            detail: extra ? `${EXCLUSION_TEXT[reason]} ${extra}` : EXCLUSION_TEXT[reason],
        });
    };

    for (const c of candidates) {
        if (c.isChild) {
            exclude(c, 'child_project');
            continue;
        }
        if (!c.customerId) {
            exclude(c, 'no_customer');
            continue;
        }

        // Rot ist keine Option, die man im Lauf anhaken koennte. Gelb schon —
        // aber nur, wenn der Nutzer das ausdruecklich gewaehlt hat.
        if (!c.check) {
            exclude(c, 'check_missing');
            continue;
        }
        if (c.check.traffic === 'red') {
            const reds = c.check.findings.filter((f) => f.traffic === 'red');
            // "Keine offene Position" ist im Lauf kein Datenproblem, sondern
            // das gewoehnliche Ergebnis: an diesem Projekt ist gerade nichts
            // abzurechnen. Als "Projektcheck gesperrt (rot)" gemeldet klingt es
            // nach einem Fehler, den jemand beheben muesste — und verdeckt die
            // Projekte, bei denen das wirklich so ist. Nur wenn NUR dieser
            // Befund rot ist; steht daneben ein echter, hat der Vorrang.
            if (reds.length && reds.every((f) => f.rule === 'no_open_positions')) {
                exclude(c, 'nothing_open');
                continue;
            }
            const first = reds[0];
            exclude(c, 'check_red', first ? `Grund: ${first.title}.` : undefined);
            continue;
        }
        if (c.check.traffic === 'yellow' && !params.includeYellow) {
            exclude(c, 'check_yellow', `${c.check.findings.length} Befund(e).`);
            continue;
        }

        const all = computeOpenPositions(c.services, c.reservations ?? new Map());
        if (all.length === 0) {
            exclude(c, 'nothing_open');
            continue;
        }

        // Der Zeitraum kappt die MENGEN, nicht nur die Projektauswahl — und
        // zwar mit DERSELBEN Funktion wie die einzelne Rechnung.
        //
        // Hier stand eine zweite, eigene Umsetzung derselben Regel. Sie kannte
        // die Zuordnung zu den Protokolleintraegen nicht und hatte keine Option
        // fuer Mengen ohne Datum: eine direkt am Leistungsstand korrigierte
        // Menge gehoert zu keinem Zeitraum und war ueber den Lauf damit
        // ueberhaupt nicht abrechenbar — das Projekt fiel dauerhaft als "nichts
        // offen" heraus. Zwei Umsetzungen derselben Regel laufen ausserdem
        // frueher oder spaeter auseinander.
        const positions: OpenPosition[] =
            params.periodFrom || params.periodTo
                ? computeOpenPositions(c.services, c.reservations ?? new Map(), {
                      from: params.periodFrom,
                      to: params.periodTo,
                      serviceLog: c.serviceLog ?? [],
                      includeUndated: params.includeUndated,
                      invoices: c.invoices,
                  })
                : all;

        if (positions.length === 0) {
            exclude(c, 'outside_period');
            continue;
        }

        const netCents = sumCents(positions.map((p) => p.netCents));
        if (params.minNetCents && netCents < params.minNetCents) {
            exclude(c, 'below_minimum', `Netto ${netCents} Cent.`);
            continue;
        }

        usable.push({
            projectId: c.projectId,
            projectNumber: c.projectNumber,
            projectName: c.projectName,
            positions,
            netCents,
            traffic: c.check.traffic,
            findingCount: c.check.findings.length,
            retentionPercent: c.retentionPercent,
            customerId: c.customerId,
            customerName: c.customerName,
            cumulative: c.cumulative === true,
        });
    }

    // Gruppieren. 'customer' ist die echte Sammelrechnung ueber mehrere
    // Projekte; 'project' erzeugt je Projekt einen eigenen Beleg.
    //
    // Kumulative Projekte stehen IMMER fuer sich, unabhaengig von der Wahl:
    // ein kumulativer Beleg schreibt den Gesamtstand eines Projekts fort und
    // zieht dessen Vorrechnungen ab. Ueber mehrere Projekte hinweg gibt es
    // diesen Stand nicht — und ein fortschreitendes Projekt mit einem
    // kumulativen in einen Beleg zu legen ergaebe zwei Rechenarten in einem
    // Dokument.
    const buckets = new Map<string, typeof usable>();
    for (const p of usable) {
        const key = p.cumulative
            ? `project:${p.projectId}`
            : params.groupBy === 'project'
              ? p.projectId
              : p.customerId;
        const list = buckets.get(key);
        if (list) list.push(p);
        else buckets.set(key, [p]);
    }

    const groups: BillingRunGroup[] = [];
    for (const [key, projects] of buckets) {
        const lines: InvoiceLine[] = [];
        let pos = 1;

        // Bei mehreren Projekten bekommt jedes eine Abschnittsueberschrift.
        // Bei einem einzigen waere sie nur Rauschen.
        const withSections = projects.length > 1;

        for (const project of projects) {
            if (withSections) {
                lines.push({
                    id: makeLineId(),
                    kind: 'SECTION',
                    // NUR der Projektname. Der Titel ist eine Andruckangabe —
                    // er steht so im PDF —, und die Projektnummer ist ein
                    // Schluessel unseres Betriebs, den der Auftraggeber nicht
                    // zuordnen kann (gleiche Regel wie fuer die Projektzeile im
                    // Belegkopf, invoiceLogic::formatProjectRefNameOnly). Ohne
                    // Namen bleibt die Nummer als Notbehelf — eine unbenannte
                    // Abschnittsueberschrift waere schlimmer.
                    //
                    // Innen aendert sich nichts: line.projectNumber traegt die
                    // Nummer unveraendert weiter, DATEV bucht ueber sie.
                    title: project.projectName || project.projectNumber,
                    projectId: project.projectId,
                    projectNumber: project.projectNumber,
                    projectName: project.projectName,
                } as InvoiceLine);
            }
            for (const p of project.positions) {
                lines.push({
                    id: makeLineId(),
                    kind: 'SERVICE',
                    posNumber: String(pos++),
                    projectId: project.projectId,
                    projectNumber: project.projectNumber,
                    projectName: project.projectName,
                    serviceId: p.service.id,
                    lvPosition: p.service.lvPosition,
                    clientLvPosition: p.service.clientLvPosition,
                    title: p.service.title,
                    unit: p.service.unit,
                    quantity: p.openQty,
                    unitPriceCents: p.unitPriceCents,
                    netCents: p.netCents,
                    taxRateBp: defaultTaxRateBp,
                    // taxCategoryCode, nicht taxCategory. Der falsche Feldname
                    // rutscht durch die Typzusicherung und bleibt zur Laufzeit
                    // undefined; groupTaxes faellt dann auf die Voreinstellung
                    // zurueck. Bei 19 % ist das zufaellig derselbe Wert — bei
                    // Reverse Charge waere es der falsche.
                    taxCategoryCode: runTaxCategory,
                } as InvoiceLine);
            }
        }

        const taxGroups = groupTaxes(lines);
        const netCents = sumCents(projects.map((p) => p.netCents));
        const taxCents = sumCents(taxGroups.map((g) => g.taxCents));

        groups.push({
            key,
            customerId: projects[0].customerId,
            customerName: projects[0].customerName,
            projects,
            lines,
            netCents,
            taxCents,
            grossCents: netCents + taxCents,
            traffic: worstTraffic(projects.map((p) => p.traffic)),
            findingCount: projects.reduce((s, p) => s + p.findingCount, 0),
            // Kumulative Projekte bilden je eine eigene Gruppe, das Kennzeichen
            // ist deshalb ueber alle enthaltenen Projekte einheitlich.
            cumulative: projects.some((p) => p.cumulative),
            // Hier und nicht erst beim Anlegen: der Trockenlauf soll denselben
            // Befund zeigen wie die Ausfuehrung ihn spaeter meldet.
            retention: retentionForGroup(projects),
        });
    }

    // Groesste Betraege zuerst — dort lohnt die Pruefung am meisten.
    groups.sort((a, b) => b.netCents - a.netCents);

    return {
        groups,
        excluded,
        totals: {
            groups: groups.length,
            projects: usable.length,
            netCents: sumCents(groups.map((g) => g.netCents)),
            grossCents: sumCents(groups.map((g) => g.grossCents)),
        },
    };
};

/**
 * Leistungszeitraum eines Entwurfs.
 *
 * Ohne gewaehlten Zeitraum spannt er sich ueber die tatsaechlichen
 * Leistungsdaten — der Leistungszeitraum ist nach §14 Abs. 4 Nr. 6 UStG
 * Pflichtangabe und darf nicht ersatzweise das Rechnungsdatum sein.
 *
 * KUMULATIVE Gruppen folgen einer eigenen Regel: ihr Beleg nennt den gesamten
 * Leistungsstand bis zum Stichtag. Der gewaehlte Zeitraumbeginn waere dort eine
 * falsche Angabe — er deckte die aelteren Leistungen nicht ab, die der Beleg
 * mit abrechnet. Massgeblich ist deshalb erste-Leistung-bis-Stichtag, und der
 * gewaehlte Beginn wird bewusst uebergangen.
 */
export const deliveryPeriodForGroup = (
    group: BillingRunGroup,
    candidates: BillingCandidate[],
    params: BillingRunParams,
): { from?: string; to?: string } => {
    const ids = new Set(group.projects.map((p) => p.projectId));
    const log = candidates.filter((c) => ids.has(c.projectId)).flatMap((c) => c.serviceLog ?? []);
    // Auf den Kalendertag kuerzen: from/to landen als deliveryDateFrom/To am
    // Beleg. Ein Zeitstempel dort wuerde jede spaetere Pruefung gegen eine
    // Tagesgrenze verfaelschen — derselbe Fehler, nur eine Stufe spaeter.
    const dates = log
        // Buchungen tragen keinen Leistungstag bei: der Tag, an dem jemand
        // eine Menge verbucht hat, gehoert nicht in den Leistungszeitraum.
        .filter((t) => !isPostingEntry(t))
        // Der Berliner Tag — derselbe, nach dem die Positionen des Belegs
        // ihren Zeitraum zaehlen (quantityInPeriod).
        .map((t) => buchungstag(t.date))
        .filter((d): d is string => !!d)
        .sort();

    if (group.cumulative) {
        // Ohne gewaehlten Stichtag ist es der letzte protokollierte Tag: bis
        // dahin reicht der Stand, den die Positionen ausweisen.
        const to = params.periodTo || dates[dates.length - 1];
        return { from: cumulativeDeliveryStart(log, to), to };
    }

    if (params.periodFrom || params.periodTo) {
        return { from: params.periodFrom, to: params.periodTo };
    }
    return { from: dates[0], to: dates[dates.length - 1] };
};

/**
 * Vermerk fuer einen Entwurf, dem der Leistungszeitraum fehlt.
 *
 * Der kumulative Zweig leitet Beginn und Stichtag aus dem Protokoll ab. Enthaelt
 * es keinen datierten Eintrag und hat niemand einen Zeitraum gewaehlt, entsteht
 * ein Entwurf ganz ohne Leistungszeitraum — nach §14 Abs. 4 Nr. 6 UStG eine
 * fehlende Pflichtangabe. Die Validierung beim Ausstellen faengt das ab, aber
 * erst dort: der Nutzer erfaehrt es dann Tage spaeter und einzeln je Beleg,
 * statt am Ende des Laufs auf einen Blick.
 *
 * Kein Grund, den Lauf zu blockieren — der Entwurf ist im Uebrigen richtig, und
 * das Datum nachzutragen ist Sache des Bearbeiters. Deshalb ein Hinweis und
 * keine Ausnahme.
 */
export const MISSING_DELIVERY_PERIOD_NOTE = 'Leistungszeitraum fehlt — vor dem Ausstellen setzen.';

export const deliveryPeriodNote = (period: {
    from?: string;
    to?: string;
}): string | undefined =>
    // Einer von beiden genuegt: ein Beleg mit blossem Leistungszeitpunkt ("bis")
    // ist eine vollstaendige Angabe, kein Mangel.
    period.from || period.to ? undefined : MISSING_DELIVERY_PERIOD_NOTE;

/** Kurzfassung des Ausschlussberichts, gruppiert nach Grund. */
export const summarizeExclusions = (
    excluded: BillingRunExclusion[],
): Array<{ reason: ExclusionReason; count: number; text: string }> => {
    const counts = new Map<ExclusionReason, number>();
    for (const e of excluded) counts.set(e.reason, (counts.get(e.reason) ?? 0) + 1);
    return [...counts.entries()]
        .map(([reason, count]) => ({ reason, count, text: EXCLUSION_TEXT[reason] }))
        .sort((a, b) => b.count - a.count);
};
